import './CsvManagement.scss';
import dayjs from 'dayjs';
import {
  IconButton,
  Icons,
  notify,
  PendingContent,
  Spinner,
  Table,
} from 'plume-ui';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { STANDARD_DATE_FORMAT } from '../../config';
import { DependencyContainer } from '../../DependencyContainer';
import { useFeaturePermission } from '../../hooks/useFeaturePermission';
import { useFetchData } from '../../hooks/useFetchData';
import { activePartnerAtom } from '../../state';
import { CsvFileSummary } from '../../types';
import { Permission } from '../../userPermissionConfig';
import FormattedMessage from '../../utils/components/FormattedMessage';
import { AdjustableComponent } from '../../utils/hooks/useClasses';
import UploadFile from '../UploadFile/UploadFile';

type CsvManagementProps = {
  callback?: (dat: CsvFileSummary) => void;
};

type CsvManagementStyles = {};

const { csvLocationsFileService } = new DependencyContainer();

const CsvManagement: AdjustableComponent<
  CsvManagementProps,
  CsvManagementStyles
> = ({ callback }) => {
  const { hasPermission } = useFeaturePermission();
  const { t } = useTranslation();

  const csvTableHeader: Record<string, unknown>[] = [
    {
      name: t('monitorAdd.uploadCsvModal.fileName'),
      fieldName: 'name',
      sortable: true,
      render: (csv: CsvFileSummary) => (
        <div
          onClick={() => {
            if (callback) {
              callback(csv);
            }
          }}
          className="CsvManagement__table__link"
        >
          {csv.name}
        </div>
      ),
    },
    {
      name: t('monitorAdd.uploadCsvModal.updatedAt'),
      fieldName: 'updatedAt',
      sortable: true,
      render: (csv: CsvFileSummary) => (
        <>{dayjs.utc(csv.updatedAt).local().format(STANDARD_DATE_FORMAT)}</>
      ),
    },
    {
      name: t('monitorAdd.uploadCsvModal.size'),
      fieldName: 'size',
      sortable: true,
      render: (csv: CsvFileSummary) => <>{csv.size} B</>,
    },
    {
      name: t('monitorAdd.uploadCsvModal.action'),
      render: (csv: CsvFileSummary) => (
        <IconButton
          onClick={async () => {
            await deleteCSV(csv);
          }}
          disabled={!hasPermission(Permission.DeleteIncludeExcludeFiles)}
          icon={<Icons.TrashIcon />}
        />
      ),
    },
  ];

  const activePartner = useRecoilValue(activePartnerAtom);
  const [
    loadingCsvList,
    csvTable,
    error,
    runFetch,
    loadedCsvList,
  ] = useFetchData<CsvFileSummary[], any>(
    async () => {
      if (!activePartner?.id) return;
      return await csvLocationsFileService.getCsvLocationsFileList(
        activePartner.id,
      );
    },
    undefined,
    [activePartner],
    () => activePartner === undefined,
  );

  const deleteCSV = async (csvToDelete: CsvFileSummary) => {
    if (activePartner?.id && csvToDelete.file_id) {
      return csvLocationsFileService
        .deleteCsvLocationsFile(activePartner.id, csvToDelete.file_id)
        .then(() => {
          notify({
            type: 'success',
            title: t('success'),
            body: t('monitorAdd.csvUpload.deleteSuccess'),
          });
          return runFetch();
        })
        .catch(() => {
          notify({
            type: 'error',
            title: t('error'),
            body: t('monitorAdd.csvUpload.deleteError'),
          });
        });
    }
  };

  return (
    <>
      <UploadFile
        callback={(dat: any) => {
          if (callback) {
            callback(dat);
          }
        }}
      />

      <PendingContent
        loading={loadingCsvList}
        loader={Spinner}
        isError={error}
        transparent
      >
        <div className="CsvManagement__recent-files">
          <FormattedMessage id="monitorAdd.uploadCsvModal.recentFiles" />
        </div>

        {loadedCsvList && (
          <>
            {!csvTable?.length ? (
              <div className="CsvManagement__no-recent-files">
                <div className="CsvManagement__no-recent-files__image">
                  <img
                    src={'/images/png/noData.png'}
                    alt="no recent files"
                    width="176.5"
                  />
                </div>
                <div className="CsvManagement__no-recent-files__text">
                  <FormattedMessage id="uploadFile.noRecentFiles" />
                </div>
              </div>
            ) : (
              <div className="CsvManagement__stickytable">
                <Table
                  dataRows={csvTable as any}
                  headerRow={csvTableHeader}
                  truncateCellContent={false}
                  stickyHeader
                />
              </div>
            )}
          </>
        )}
      </PendingContent>
    </>
  );
};

export default memo(CsvManagement);
